// UserProfileJsonLd.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';
import { useTranslation } from 'react-i18next';

import { ProfileListType } from '../resource/profileConstants.js';
import getProfileTdk from '../resource/getProfileTdk.js';
import {
  getPerson,
  getFlixVideoObject,
  getFlixPreviewVideoObject,
  getInteractionCounter,
  getStoryVideoObject,
  getStoryPreviewVideoObject,
  getShortSchemaObject,
  getPostImageObject,
} from '../resource/SchemaObject.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  getPoster,
  getAssetManifest,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';

export const UserProfileJsonLd = ({
  username,
  biography,
  type,
  userId,
  tabs,
  storyDataList,
  flixDataList,
  imageDataList,
  shortsDataList,
}) => {
  const { t } = useTranslation();

  const isFirstTabActived = type === tabs[0];
  const { displayTitle: pageName } = getProfileTdk({
    username,
    biography,
    listType: type,
    t,
  });

  const result = useMemo(() => {
    return (
      <Helmet>
        <script type="application/ld+json" helmetkey="jsonld">
          {serialize(
            {
              '@context': 'https://schema.org',
              '@graph': [
                getPerson({
                  id: userId,
                  username,
                  biography,
                  hasOfferCatalog: [
                    {
                      '@id': `https://swag.live/user/${userId}#stories`,
                      url: `https://swag.live/user/${userId}`,
                    },
                    {
                      '@id': `https://swag.live/user/${userId}/flix#flixes`,
                      url: `https://swag.live/user/${userId}/flix`,
                    },
                    {
                      '@id': `https://swag.live/user/${userId}/image#images`,
                      url: `https://swag.live/user/${userId}/image`,
                    },
                  ],
                }),
                {
                  '@type': 'ProfilePage',
                  '@id': `https://swag.live/user/${userId}/${type}#webpage`,
                  url: `https://swag.live/user/${userId}/${type}`,
                  name: pageName,
                  breadcrumb: {
                    '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
                  },
                  mainEntity: {
                    '@id': `https://swag.live/user/${userId}#person`,
                  },
                  significantLinks: tabs.map((tab, index) => {
                    if (index === 0) {
                      return `https://swag.live/user/${userId}`;
                    }
                    return `https://swag.live/user/${userId}/${tab}`;
                  }),
                },
                {
                  '@type': 'BreadcrumbList',
                  '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: t('seo-home-title', {
                        ns: TranslationNamespace.SEO,
                      }),
                      item: 'https://swag.live',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: getProfileTdk({
                        username,
                        biography,
                        listType: tabs[0],
                        t,
                      }).displayTitle,
                      item: `https://swag.live/user/${userId}`,
                    },
                    !isFirstTabActived && {
                      '@type': 'ListItem',
                      position: 3,
                      name: pageName,
                      item: `https://swag.live/user/${userId}/${type}`,
                    },
                  ].filter(a => a),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}#person`,
                  url: `https://swag.live/user/${userId}`,
                  itemListElement: [
                    ...storyDataList,
                    ...flixDataList,
                    ...shortsDataList,
                    ...imageDataList,
                  ].map((messageData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${messageData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/${ProfileListType.STORY}#stories`,
                  url: `https://swag.live/user/${userId}/${ProfileListType.STORY}`,
                  itemListElement: storyDataList.map((messageData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${messageData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/${ProfileListType.FLIX}#flixes`,
                  url: `https://swag.live/user/${userId}/${ProfileListType.FLIX}`,
                  itemListElement: flixDataList.map((messageData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${messageData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/${ProfileListType.SHORT}#shorts`,
                  url: `https://swag.live/user/${userId}/${ProfileListType.SHORT}`,
                  itemListElement: shortsDataList.map((messageData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${messageData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/${ProfileListType.IMAGE}#images`,
                  url: `https://swag.live/user/${userId}/${ProfileListType.IMAGE}`,
                  itemListElement: imageDataList.map((messageData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${messageData.id}#preview`,
                    },
                  })),
                },
                ...storyDataList.map(messageData => {
                  return getStoryPreviewVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    username,
                    caption: messageData.caption,
                    durationSecs: messageData.media?.duration,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                  });
                }),
                ...storyDataList.map(messageData => {
                  return getStoryVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    username,
                    caption: messageData.caption,
                    durationSecs: messageData.media?.duration,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    isFree: !messageData.unlockPrice,
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ]
                      .filter(s => s.count)
                      .map(getInteractionCounter),
                  });
                }),
                ...flixDataList.map(messageData => {
                  return getFlixPreviewVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    title: messageData.title,
                    description: messageData.caption || messageData.title,
                    durationSecs: 10,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                  });
                }),
                ...flixDataList.map(messageData => {
                  return getFlixVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    title: messageData.title,
                    description: messageData.caption || messageData.title,
                    durationSecs: messageData.media?.duration || 600,
                    postedAtUnix: messageData.postedAtUnix,
                    isFree: !messageData.unlockPrice,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ]
                      .filter(s => s.count)
                      .map(getInteractionCounter),
                  });
                }),
                ...shortsDataList.map(messageData => {
                  const firstAssetId = messageData?.assetIds?.[0];
                  return getShortSchemaObject({
                    id: messageData.id,
                    title: messageData.title,
                    description: messageData.caption || messageData.title,
                    durationSecs: messageData.media?.duration,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    username,
                    postedAtUnix: messageData.postedAtUnix,
                    isFree: !messageData.unlockPrice,
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ],
                    encodings: [
                      {
                        '@id': `https://swag.live/shorts/${messageData.id}#video-dash`,
                      },
                    ],
                    associatedMedia: messageData.sourceId
                      ? {
                          '@id': `https://swag.live/post/${messageData.sourceId}#preview`,
                        }
                      : undefined,
                    thumbnailUrl: getPoster({
                      id: messageData.id,
                      size: '1024x1024',
                      format: MediaAssetFormat.JPG,
                      preset: MediaPreset.SD,
                    }),
                    contentUrl: firstAssetId
                      ? getAssetManifest({
                          id: messageData.id,
                          assetId: firstAssetId,
                          format: MediaAssetFormat.M3U8,
                          preset: MediaPreset.SD,
                        })?.href
                      : undefined,
                  });
                }),
                ...imageDataList.map(messageData => {
                  return getPostImageObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    description: messageData.caption,
                    durationSecs: messageData.media?.duration,
                    postedAtUnix: messageData.postedAtUnix,
                    isFree: !messageData.unlockPrice,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ]
                      .filter(s => s.count)
                      .map(getInteractionCounter),
                  });
                }),
              ],
            },
            { isJSON: true }
          )}
        </script>
      </Helmet>
    );
  }, [
    biography,
    flixDataList,
    imageDataList,
    isFirstTabActived,
    pageName,
    shortsDataList,
    storyDataList,
    t,
    tabs,
    type,
    userId,
    username,
  ]);

  return result;
};

UserProfileJsonLd.propTypes = {
  type: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  biography: PropTypes.string,
  tabs: PropTypes.array,
  storyDataList: PropTypes.array,
  flixDataList: PropTypes.array,
  imageDataList: PropTypes.array,
  shortsDataList: PropTypes.array,
};

UserProfileJsonLd.defaultProps = {
  type: ProfileListType.STORY,
  userId: null,
  username: '',
  biography: '',
  tabs: [],
  storyDataList: [],
  flixDataList: [],
  imageDataList: [],
  shortsDataList: [],
};

export default UserProfileJsonLd;
